// ═══════════════════════════════════════════════════════════════════
//  modules/chat/ChatPage.js
//  ChatPage
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from "react";
import { API_BASE } from "../../socle/api";
import { THEME } from "../../socle/theme";
import { ChatAnalysisTab } from "./ChatAnalysisTab";

// ═══════════════════════ PAGE CHAT EN JEU ═════════════════════════════
export function ChatPage({ token, user }) {
  const [tab, setTab] = useState("history");
  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(50);
  const [server, setServer] = useState("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  const loadHistory = useCallback(async (newOffset = 0) => {
    setLoadingHistory(true);
    try {
      const params = new URLSearchParams({ limit, offset: newOffset });
      if (server) params.set("server", server);
      if (search) params.set("search", search);
      const res = await fetch(`${API_BASE}/api/chat/history?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.unavailable) { setUnavailable(true); setMessages([]); setTotal(0); }
      else { setUnavailable(false); setMessages(data.messages || []); setTotal(data.total || 0); setOffset(newOffset); }
    } catch (err) { console.error(err); }
    setLoadingHistory(false);
  }, [limit, search, server, token]);

  const loadStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const res = await fetch(`${API_BASE}/api/chat/stats`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (data.unavailable) { setUnavailable(true); setStats(null); }
      else { setUnavailable(false); setStats(data); }
    } catch (err) { console.error(err); }
    setLoadingStats(false);
  }, [token]);

  useEffect(() => {
    if (tab === "history") loadHistory(0);
    else if (tab === "stats") loadStats();
    // l'onglet "ia" charge ses propres données
  }, [tab, server, search, loadHistory, loadStats, limit]);

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;
  const serverBadgeColor = (srv) => srv === "spawn" ? THEME.emerald : THEME.accent;
  const serverBadgeBg = (srv) => srv === "spawn" ? THEME.emeraldDim : THEME.accentDim;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <h2 style={{ color: THEME.text, fontSize: 18, fontWeight: 700, margin: 0 }}>💬 Chat en jeu</h2>

      {unavailable && (
        <div style={{ background: THEME.amberDim, border: `1px solid ${THEME.amber}`, borderRadius: 8, padding: "10px 14px", color: THEME.amber, fontSize: 13 }}>
          ⏳ La table de messages n'est pas encore disponible. Les données apparaîtront ici une fois le plugin Chat installé.
        </div>
      )}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {[["history", "📜 Historique"], ["stats", "📊 Statistiques"], ["ia", "🤖 Analyses IA"]].map(([id, lbl]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{
              background: tab === id ? (id === "ia" ? THEME.purpleDim : THEME.accentDim) : "transparent",
              border: `1px solid ${tab === id ? (id === "ia" ? THEME.purple : THEME.accent) : THEME.border}`,
              color: tab === id ? (id === "ia" ? THEME.purple : THEME.accent) : THEME.textMuted,
              borderRadius: 8, padding: "7px 16px", cursor: "pointer", fontSize: 13, fontWeight: tab === id ? 700 : 400,
            }}>{lbl}</button>
        ))}
      </div>

      {tab === "history" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {[["", "Tous"], ["spawn", "Spawn"], ["boxed", "Boxed"], ["discord", "Discord"]].map(([val, lbl]) => (
              <button key={val} onClick={() => setServer(val)} style={{ background: server === val ? THEME.accentDim : "transparent", border: `1px solid ${server === val ? THEME.accent : THEME.border}`, color: server === val ? THEME.accent : THEME.textMuted, borderRadius: 6, padding: "5px 14px", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{lbl}</button>
            ))}
            <div style={{ display: "flex", gap: 6, flex: 1, minWidth: 200 }}>
              <input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setSearch(searchInput)} placeholder="Rechercher pseudo ou message..." style={{ flex: 1, background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 6, padding: "6px 12px", color: THEME.text, fontSize: 13, outline: "none" }} />
              <button onClick={() => setSearch(searchInput)} style={{ background: THEME.accentDim, border: `1px solid ${THEME.accent}`, color: THEME.accent, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>🔍</button>
            </div>
          </div>
          {loadingHistory ? <div style={{ color: THEME.textMuted, padding: 20 }}>Chargement...</div> : (
            <>
              <div style={{ color: THEME.textMuted, fontSize: 12 }}>{total} message(s) · Page {currentPage}/{Math.max(1, totalPages)}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {messages.length === 0 && !unavailable && <div style={{ color: THEME.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>Aucun message trouvé.</div>}
                {messages.map((m) => (
                  <div key={m.id} style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "flex-start", gap: 12 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                        <span style={{ color: THEME.gold, fontWeight: 700, fontSize: 13 }}>{m.username}</span>
                        {m.target && (
                          <span style={{ color: THEME.textMuted, fontSize: 12 }}>
                            → <span style={{ color: THEME.gold, fontWeight: 700 }}>{m.target}</span>
                          </span>
                        )}
                        <span style={{ background: serverBadgeBg(m.server), color: serverBadgeColor(m.server), fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4 }}>{m.server}</span>
                        {m.channel && m.channel !== "global" && <span style={{ background: THEME.purpleDim, color: THEME.purple, fontSize: 10, fontWeight: 700, padding: "1px 6px", borderRadius: 4 }}>{m.channel}</span>}
                        <span style={{ color: THEME.textDim, fontSize: 11 }}>{new Date(m.created_at).toLocaleString("fr")}</span>
                      </div>
                      <div style={{ color: THEME.text, fontSize: 13 }}>{m.message}</div>
                    </div>
                  </div>
                ))}
              </div>
              {totalPages > 1 && (
                <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 8 }}>
                  <button onClick={() => loadHistory(Math.max(0, offset - limit))} disabled={offset === 0} style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: offset === 0 ? THEME.textDim : THEME.text, borderRadius: 6, padding: "6px 14px", cursor: offset === 0 ? "not-allowed" : "pointer", fontSize: 13 }}>← Préc.</button>
                  <button onClick={() => loadHistory(offset + limit)} disabled={offset + limit >= total} style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, color: offset + limit >= total ? THEME.textDim : THEME.text, borderRadius: 6, padding: "6px 14px", cursor: offset + limit >= total ? "not-allowed" : "pointer", fontSize: 13 }}>Suiv. →</button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "stats" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {loadingStats ? <div style={{ color: THEME.textMuted, padding: 20 }}>Chargement...</div> : stats ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12 }}>
                <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 14 }}>
                  <div style={{ color: THEME.textMuted, fontSize: 11, textTransform: "uppercase" }}>💬 Messages aujourd'hui</div>
                  <div style={{ color: THEME.accent, fontSize: 26, fontWeight: 800, marginTop: 4 }}>{stats.total_today}</div>
                </div>
                <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 14 }}>
                  <div style={{ color: THEME.textMuted, fontSize: 11, textTransform: "uppercase" }}>📅 Messages cette semaine</div>
                  <div style={{ color: THEME.purple, fontSize: 26, fontWeight: 800, marginTop: 4 }}>{stats.total_week}</div>
                </div>
              </div>
              {stats.byServer.length > 0 && (
                <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 16 }}>
                  <div style={{ color: THEME.textMuted, fontSize: 12, fontWeight: 700, marginBottom: 10 }}>PAR SERVEUR</div>
                  {stats.byServer.map((s) => (
                    <div key={s.server} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${THEME.border}`, fontSize: 13 }}>
                      <span style={{ color: serverBadgeColor(s.server), fontWeight: 600 }}>{s.server}</span>
                      <span style={{ color: THEME.text, fontWeight: 700 }}>{Number(s.total).toLocaleString("fr")}</span>
                    </div>
                  ))}
                </div>
              )}
              {stats.byHour.length > 0 && (
                <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 16 }}>
                  <div style={{ color: THEME.textMuted, fontSize: 12, fontWeight: 700, marginBottom: 10 }}>ACTIVITÉ PAR HEURE (7 derniers jours)</div>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 80 }}>
                    {Array.from({ length: 24 }, (_, h) => {
                      const found = stats.byHour.find(b => b.heure === h);
                      const val = found ? Number(found.total) : 0;
                      const max = Math.max(1, ...stats.byHour.map(b => Number(b.total)));
                      const pct = Math.round((val / max) * 100);
                      return (
                        <div key={h} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                          <div style={{ width: "100%", background: val > 0 ? THEME.accent : THEME.surface, borderRadius: "2px 2px 0 0", height: `${Math.max(2, pct)}%`, minHeight: val > 0 ? 4 : 2 }} title={`${h}h : ${val} messages`} />
                          {h % 4 === 0 && <div style={{ color: THEME.textDim, fontSize: 8 }}>{h}h</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {stats.topPlayers.length > 0 && (
                <div style={{ background: THEME.panel, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 16 }}>
                  <div style={{ color: THEME.textMuted, fontSize: 12, fontWeight: 700, marginBottom: 10 }}>TOP 10 JOUEURS (30 jours)</div>
                  {stats.topPlayers.map((p, i) => (
                    <div key={p.username} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${THEME.border}`, fontSize: 13 }}>
                      <span style={{ color: i < 3 ? THEME.gold : THEME.textMuted, fontWeight: 700, width: 20 }}>#{i + 1}</span>
                      <span style={{ color: THEME.text, flex: 1 }}>{p.username}</span>
                      <span style={{ color: THEME.accent, fontWeight: 700 }}>{Number(p.total).toLocaleString("fr")}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : !unavailable && <div style={{ color: THEME.textDim, fontSize: 13, padding: 20, textAlign: "center" }}>Aucune statistique disponible.</div>}
        </div>
      )}

      {tab === "ia" && <ChatAnalysisTab token={token} user={user} />}
    </div>
  );
}
